import React, { useState } from "react";
import {
    alpha,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { parseWithOffset } from "../../../utils/dayjsUtils";
import { FONTS, FORM_LABEL_SX } from "../../../constants/styles";

interface BlockTimeModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (start: dayjs.Dayjs, end: dayjs.Dayjs, note?: string) => void;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    mode?: "create" | "edit";
    initialNote?: string;
}

const BlockTimeModal: React.FC<BlockTimeModalProps> = ({
    open,
    onClose,
    onConfirm,
    start: initialStart,
    end: initialEnd,
    mode = "create",
    initialNote = "",
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const [start, setStart] = useState(initialStart.format("HH:mm"));
    const [end, setEnd] = useState(initialEnd.format("HH:mm"));
    const [customName, setCustomName] = useState(initialNote);
    const [error, setError] = useState("");

    const handleTimeChange = (
        setter: React.Dispatch<React.SetStateAction<string>>,
        value: string
    ) => {
        setter(value);
        setError("");
    };

    const handleSubmit = () => {
        const baseStart = dayjs(initialStart).second(0).millisecond(0);
        let baseEnd = dayjs(initialEnd).second(0).millisecond(0);

        if (!baseEnd.isAfter(baseStart)) {
            baseEnd = baseEnd.add(1, "day");
        }

        const [sH, sM] = start.split(":").map(Number);
        const [eH, eM] = end.split(":").map(Number);

        const newStart = baseStart.clone().set("hour", sH).set("minute", sM);
        let newEnd = baseStart.clone().set("hour", eH).set("minute", eM);

        if (!newEnd.isAfter(newStart)) {
            newEnd = newEnd.add(1, "day");
        }

        if (mode !== "edit") {
            if (newStart.isBefore(baseStart)) {
                setError(
                    "Start time cannot be earlier than the original time of the slot"
                );
                return;
            }

            if (newEnd.isAfter(baseEnd)) {
                setError(
                    "End time cannot be later than the original time of the slot"
                );
                return;
            }
        }

        onConfirm(
            parseWithOffset(newStart),
            parseWithOffset(newEnd),
            customName
        );
        onClose();
    };

    return (
        <Dialog
            className="force-dynamic-translation"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    padding: 3,
                    textAlign: "center",
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={700}
                    fontSize="18px"
                >
                    {mode === "edit" ? "Edit" : "Add Personal Time"}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography
                        fontWeight={400}
                        fontSize="14px"
                        color="text.secondary"
                    >
                        {mode === "edit"
                            ? "Update this blocked slot"
                            : "This slot will be marked as unavailable for bookings"}
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Stack spacing={0.5} flex={1}>
                            <Typography sx={FORM_LABEL_SX}>
                                Start Time
                            </Typography>
                            <TextField
                                type="time"
                                value={start}
                                onChange={(e) =>
                                    handleTimeChange(setStart, e.target.value)
                                }
                                inputProps={{
                                    step: 300,
                                    "aria-label": "Start Time",
                                }}
                                autoFocus
                                sx={{
                                    minWidth: "80px",
                                    "& input::-webkit-calendar-picker-indicator":
                                        {
                                            display: "none",
                                            appearance: "none",
                                        },
                                }}
                            />
                        </Stack>
                        <Stack spacing={0.5} flex={1}>
                            <Typography sx={FORM_LABEL_SX}>
                                End Time
                            </Typography>
                            <TextField
                                type="time"
                                value={end}
                                onChange={(e) =>
                                    handleTimeChange(setEnd, e.target.value)
                                }
                                inputProps={{
                                    step: 300,
                                    "aria-label": "End Time",
                                }}
                                sx={{
                                    minWidth: "80px",
                                    "& input::-webkit-calendar-picker-indicator":
                                        {
                                            display: "none",
                                            appearance: "none",
                                        },
                                }}
                            />
                        </Stack>
                    </Stack>
                    <Stack spacing={0.5}>
                        <Typography sx={FORM_LABEL_SX}>
                            Description (optional)
                        </Typography>
                        <TextField
                            placeholder="Gym, Break, Meeting..."
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={3}
                            inputProps={{
                                "aria-label": "Description",
                            }}
                        />
                    </Stack>
                    {error && (
                        <Typography
                            fontWeight={500}
                            fontSize="14px"
                            color="error"
                        >
                            {error}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
                <ButtonBase
                    onClick={onClose}
                    sx={{
                        borderRadius: 20,
                        px: 2,
                        py: 0.75,
                        bgcolor: "rgba(0,0,0,0.04)",
                    }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="13px"
                        fontWeight={600}
                        color="text.secondary"
                    >
                        Cancel
                    </Typography>
                </ButtonBase>
                <ButtonBase
                    onClick={handleSubmit}
                    sx={{
                        borderRadius: 20,
                        px: 2,
                        py: 0.75,
                        bgcolor: alpha(hex, 0.1),
                    }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="13px"
                        fontWeight={600}
                        color={hex}
                    >
                        {mode === "edit" ? "Save" : "Add"}
                    </Typography>
                </ButtonBase>
            </DialogActions>
        </Dialog>
    );
};

export default BlockTimeModal;
